import { LicenseStatus, OwnerUser, ownerFullName } from '../../../general/model';

// --- Контракт GET /api/crhub/laws/font_licenses (MYUA-16456) ---
// Бэкенд отдаёт лицензии с вложенным узлом файла (имя файла), владельцем (создателем)
// и модератором (moderator — вложенный объект, как owner; moderated_at — когда принято решение).

// Подмножество NodeSerializer, которое нам нужно на странице модерации.
export type ModerationNode = {
  id: number;
  name: string;
  created_at: string;
};

// Объект лицензии шрифта, как его возвращает бэкенд (FontLicenseSerializer).
export type FontLicenseModerationItem = {
  id: number;
  node: ModerationNode;
  jira_task_url: string;
  // Шрифт, к которому относится лицензия, когда это не сам загруженный файл:
  // путь записи внутри архива (`fonts/Arial.ttf`). null — файл сам является шрифтом.
  // У архива со несколькими шрифтами будет по лицензии на каждый шрифт, и они
  // отличаются только этим полем.
  filename: string | null;
  license_type: number | null;
  expiration_date: string | null;
  status: LicenseStatus;
  projects: number[];
  moderator: OwnerUser | null;
  moderated_at: string | null;
  created_at: string;
  owner: OwnerUser | null;
};

// Строка таблицы на странице модерации: плоское представление лицензии.
export type ModerationFile = {
  id: number; // id лицензии (используется в PATCH)
  node_id: number; // node.id — по нему строится ссылка на файл в Creative Hub
  file_name: string; // node.name
  // Шрифт внутри архива, к которому относится лицензия (FontLicense.filename), либо null.
  // Показывается серым рядом с именем файла, когда отличается от самого файла.
  font_file_name: string | null;
  author: string; // владелец (создатель) файла — полное имя либо username
  upload_date: string; // node.created_at — когда файл загружен
  status: LicenseStatus;
  jira_task_url: string;
  license_type: number | null; // id типа лицензии (название резолвим по справочнику)
  expiration_date: string | null; // 2999-01-01 = бессрочно; null возможен у старых записей
  projects: number[]; // id проектов (названия резолвим по справочнику)
  moderated_by: OwnerUser | null; // кто принял решение (moderator)
  moderated_at: string | null; // когда принято решение (moderated_at)
};

// Значение фильтра по статусу модерации в шапке страницы. 'ALL' — без фильтра.
export type ModerationStatusFilter = LicenseStatus | 'ALL';

// Payload решения юриста. Отправляются только изменённые поля (минимум одно).
// status без изменения не шлём: повторный APPROVED/REJECTED перезаписывает
// moderator/moderated_at, а UNDER_MODERATION бэкенд не принимает.
// Семантика «Unlimited»: projects — пустой массив (без ограничения по проектам),
// expiration_date — 2999-01-01 (бессрочно). Бэкенд (FontLicenseUpdateSerializer) принимает
// projects как список PK и expiration_date как nullable date; бессрочность отправляем датой.
// license_type/jira_task_url очистить нельзя: null и "" бэкенд не принимает (MYUA-16856).
export type UpdateFontLicensePayload = {
  status?: LicenseStatus;
  projects?: number[];
  expiration_date?: string | null;
  // PK типа из GET /laws/license_types.
  license_type?: number;
  // Полный URL таска, нормализованный через normalizeJiraUrl.
  jira_task_url?: string;
};

// Маппинг сырого объекта лицензии в строку таблицы.
export function toModerationFile(item: FontLicenseModerationItem): ModerationFile {
  return {
    id: item.id,
    node_id: item.node.id,
    file_name: item.node.name,
    font_file_name: item.filename ?? null,
    author: item.owner ? ownerFullName(item.owner) || item.owner.username : '',
    upload_date: item.node.created_at,
    status: item.status,
    jira_task_url: item.jira_task_url,
    license_type: item.license_type,
    expiration_date: item.expiration_date,
    projects: item.projects,
    moderated_by: item.moderator,
    moderated_at: item.moderated_at,
  };
}
